import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Checkbox, Input, Modal, Space, Spin, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { message } from "../../components/antdMessageBridge";
import "../../components/commonpagination/common-pagination.css";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import { useAuth } from "../../auth/AuthContext";
import CommonObj from "../../CommonObj";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";

const DEFAULT_MENU_CONFIG = [];

const normalizeRows = (rows) => {
    if (!Array.isArray(rows)) {
        return DEFAULT_MENU_CONFIG;
    }

    return rows.map((row) => ({
        menuId: Number(row?.menuId) || 0,
        menuName: row?.menuName || "",
        parentMenu: row?.parentMenu === undefined ? null : row.parentMenu,
        superAdmin: Number(row?.superAdmin) === 1 ? 1 : 0,
        manager: Number(row?.manager) === 1 ? 1 : 0,
        operator: Number(row?.operator) === 1 ? 1 : 0,
        client: Number(row?.client) === 1 ? 1 : 0,
        vendor: Number(row?.vendor) === 1 ? 1 : 0,
    }));
};

// Groups flat menu rows into a nested structure for the expandable antd Table:
// rows whose parentMenu is null/undefined are treated as category headers, and
// rows whose parentMenu matches a header's menuName become that header's children.
const buildGroupedRows = (flatRows) => {
    const groupRows = flatRows.filter(
        (row) => row.parentMenu === null || row.parentMenu === undefined
    );
    const childRows = flatRows.filter(
        (row) => row.parentMenu !== null && row.parentMenu !== undefined
    );

    const groups = groupRows.map((group) => {
        const children = childRows.filter((child) => child.parentMenu === group.menuName);
        return {
            ...group,
            isGroupRow: true,
            ...(children.length > 0 ? { children } : {}),
        };
    });

    const groupedMenuNames = new Set(groupRows.map((group) => group.menuName));
    const ungroupedChildren = childRows.filter(
        (child) => !child.parentMenu || !groupedMenuNames.has(child.parentMenu)
    );

    return [...groups, ...ungroupedChildren];
};

const UserRoleMaster = () => {
    const navigate = useNavigate();
    const { accessToken } = useAuthToken();
    const { roleUserId } = useAuth();
    const [loading, setLoading] = useState(false);
    const [rolesLoading, setRolesLoading] = useState(false);
    const [apiNoResults, setApiNoResults] = useState(false);
    const [rows, setRows] = useState(DEFAULT_MENU_CONFIG);
    const [roles, setRoles] = useState([]);
    const [tablePagination, setTablePagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const [submitting, setSubmitting] = useState(false);
    const [dirtyMenuIds, setDirtyMenuIds] = useState([]);
    const [searchText, setSearchText] = useState("");
    const dirtyMenuIdsRef = useRef(dirtyMenuIds);
    const menuNameSearchInputRef = useRef(null);

    const getModeTrackLabel = () => "user-role-master";

    const resolveRoleFieldKey = (roleName) => {
        const normalizedName = (roleName || "").trim();
        if (!normalizedName) return null;

        // Convert role label to expected menu payload key (e.g., "Super Admin" -> "superAdmin").
        const words = normalizedName.split(/\s+/);
        if (words.length === 0) return null;

        return words
            .map((word, index) => {
                const lower = word.toLowerCase();
                return index === 0 ? lower : `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
            })
            .join("");
    };

    const fetchMenus = async () => {
        // if (!accessToken) return;

        setLoading(true);
        try {
            const response = await fetch(CommonObj.Endpoints.GetMenus, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                    "X-CSRF-Token": encryptTimestamp(),
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`GetMenus failed (${response.status}): ${errorText}`);
            }

            const apiRows = normalizeRows(await response.json());
            setRows(apiRows);
            setApiNoResults(apiRows.length === 0);
        } catch (err) {
            console.log("Fetch menus error:", err);
            setRows(DEFAULT_MENU_CONFIG);
            setApiNoResults(DEFAULT_MENU_CONFIG.length === 0);
            message.error("Unable to load response");
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        // if (!accessToken) return;

        setRolesLoading(true);
        try {
            const response = await fetch(CommonObj.Endpoints.GetRoles, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                    "X-CSRF-Token": encryptTimestamp(),
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`GetRoles failed (${response.status}): ${errorText}`);
            }

            const data = await response.json();
            const normalizedRoles = Array.isArray(data)
                ? data.map((item) => ({
                    roleId: Number(item?.roleId),
                    roleName: item?.roleName || "",
                }))
                    .filter((item) => Number.isFinite(item.roleId) && item.roleName)
                : [];

            setRoles(normalizedRoles);
        } catch (err) {
            console.log("Fetch roles error:", err);
            message.error("Unable to load /api/Role/GetRoles.");
            setRoles([]);
        } finally {
            setRolesLoading(false);
        }
    };

    useEffect(() => {
        fetchMenus();
        fetchRoles();
    }, [accessToken]);

    useEffect(() => {
        setTablePagination((prev) => ({
            ...prev,
            current: 1,
        }));
    }, [rows.length]);

    useEffect(() => {
        dirtyMenuIdsRef.current = dirtyMenuIds;
    }, [dirtyMenuIds]);

    // Warn the user before leaving the Role Master screen (in-app navigation, browser
    // back/forward, or closing/refreshing the tab) while there are unsaved checkbox changes.
    useEffect(() => {
        const UNSAVED_CHANGES_WARNING = "You have unsaved changes. Are you sure you want to leave?";

        const handleBeforeUnload = (e) => {
            if (dirtyMenuIdsRef.current.length === 0) return;
            e.preventDefault();
            e.returnValue = "";
            return "";
        };

        globalThis.addEventListener("beforeunload", handleBeforeUnload);

        const originalPushState = globalThis.history.pushState.bind(globalThis.history);
        let bypassGuard = false;
        let lastPath = globalThis.location.pathname + globalThis.location.search;

        const guardedPushState = (...args) => {
            if (dirtyMenuIdsRef.current.length === 0 || bypassGuard) {
                lastPath = globalThis.location.pathname + globalThis.location.search;
                return originalPushState(...args);
            }

            // args[2] is the target url react-router just asked history to push to.
            const targetUrl = args[2];

            Modal.confirm({
                title: "Unsaved Changes",
                content: UNSAVED_CHANGES_WARNING,
                okText: "Leave",
                cancelText: "Stay",
                onOk: () => {
                    bypassGuard = true;
                    setDirtyMenuIds([]);
                    // Go through react-router's own navigate() (not the raw pushState)
                    // so its internal listeners are notified and the page actually
                    // switches, not just the address bar.
                    if (targetUrl == null) {
                        originalPushState(...args);
                    } else {
                        navigate(targetUrl);
                    }
                    lastPath = globalThis.location.pathname + globalThis.location.search;
                    bypassGuard = false;
                },
            });
        };

        globalThis.history.pushState = guardedPushState;

        const handlePopState = () => {
            if (dirtyMenuIdsRef.current.length === 0 || bypassGuard) {
                lastPath = globalThis.location.pathname + globalThis.location.search;
                return;
            }

            // The browser has already navigated to the target URL by the time popstate
            // fires, so capture it, then immediately navigate back to where the user was
            // (in sync with the router) until they confirm leaving.
            const targetPath = globalThis.location.pathname + globalThis.location.search;
            const previousPath = lastPath;

            bypassGuard = true;
            navigate(previousPath, { replace: true });
            bypassGuard = false;

            Modal.confirm({
                title: "Unsaved Changes",
                content: UNSAVED_CHANGES_WARNING,
                okText: "Leave",
                cancelText: "Stay",
                onOk: () => {
                    bypassGuard = true;
                    setDirtyMenuIds([]);
                    lastPath = targetPath;
                    navigate(targetPath);
                    bypassGuard = false;
                },
            });
        };

        globalThis.addEventListener("popstate", handlePopState);

        return () => {
            globalThis.removeEventListener("beforeunload", handleBeforeUnload);
            globalThis.removeEventListener("popstate", handlePopState);
            globalThis.history.pushState = originalPushState;
        };
    }, [navigate]);

    const buildRoleIdsFromRow = (row) => {
        const selectedRoleIds = roles
            .filter((role) => {
                const fieldKey = resolveRoleFieldKey(role.roleName);
                return fieldKey && Number(row?.[fieldKey]) === 1;
            })
            .map((role) => role.roleId);

        return selectedRoleIds.join(",");
    };

    const handleRoleToggle = (menuId, roleKey, checked) => {
        setRows((prev) =>
            prev.map((row) => {
                if (row.menuId !== menuId) return row;
                return {
                    ...row,
                    [roleKey]: checked ? 1 : 0,
                };
            })
        );
        setDirtyMenuIds((prev) => (prev.includes(menuId) ? prev : [...prev, menuId]));
    };

    const buildSubmitPayload = () => {
        const changedRows = rows.filter((row) => dirtyMenuIds.includes(row.menuId));

        return changedRows.map((row) => ({
            menuId: row.menuId,
            roleIds: buildRoleIdsFromRow(row),
            modifiedBy: Number(roleUserId) || 1,
        }));
    };

    const handleSubmit = async () => {
        // if (!accessToken) {
        //     message.error("Access token missing. Please sign in again.");
        //     return;
        // }

        if (dirtyMenuIds.length === 0) {
            message.info("No modifications detected. No changes were saved.");
            return;
        }

        const payload = buildSubmitPayload();
        setSubmitting(true);

        try {
            const response = await fetch(CommonObj.Endpoints.UpdateRoleMenuPageApiMapping, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                    "X-CSRF-Token": encryptTimestamp(),
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Batch update failed (${response.status}): ${errorText}`);
            }

            const result = await response.json();
            if (result?.success === false) {
                throw new Error(result?.message || "Batch update API returned unsuccessful response.");
            }

            setDirtyMenuIds([]);
            message.success("Role configuration updated successfully.");
            await fetchMenus();
        } catch (err) {
            console.log("Batch update menu-role mapping error:", err);
            message.error("Failed to save role configurations. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleMenuNameSearch = (selectedKeys, confirm) => {
        confirm();
        setSearchText(selectedKeys[0] || "");
    };

    const handleMenuNameSearchReset = (clearFilters, confirm) => {
        clearFilters();
        confirm();
        setSearchText("");
    };

    const staticColumns = [
        {
            title: "Menu Name",
            dataIndex: "menuName",
            key: "menuName",
            width: 320,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={menuNameSearchInputRef}
                        placeholder="Search Menu Name"
                        value={selectedKeys[0]}
                        onChange={(e) =>
                            setSelectedKeys(e.target.value ? [e.target.value] : [])
                        }
                        style={{ marginBottom: 8, display: "block" }}
                        onPressEnter={() => handleMenuNameSearch(selectedKeys, confirm)}
                    />
                    <Space>
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => handleMenuNameSearch(selectedKeys, confirm)}
                        >
                            Search
                        </Button>
                        <Button
                            size="small"
                            onClick={() => handleMenuNameSearchReset(clearFilters, confirm)}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered) => (
                <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
            ),
            filteredValue: searchText ? [searchText] : null,
            onFilterDropdownOpenChange: (visible) => {
                if (visible) {
                    setTimeout(() => menuNameSearchInputRef.current?.select(), 100);
                }
            },
            render: (menuName, record) =>
                record.isGroupRow ? <strong>{menuName}</strong> : menuName,
        },
    ];

    const roleColumns = roles
        .map((role) => {
            const fieldKey = resolveRoleFieldKey(role.roleName);
            if (!fieldKey) return null;

            return {
                title: role.roleName,
                key: `role-${role.roleId}`,
                width: 150,
                align: "center",
                render: (_, record) => {
                    if (record.isGroupRow) return null;
                    return (
                        <Checkbox
                            checked={Number(record?.[fieldKey]) === 1}
                            disabled={submitting}
                            onChange={(e) =>
                                handleRoleToggle(record.menuId, fieldKey, e.target.checked)
                            }
                        />
                    );
                },
            };
        })
        .filter(Boolean);

    const columns = [...staticColumns, ...roleColumns];

    const searchLower = searchText.trim().toLowerCase();
    const filteredRows = searchLower
        ? rows.filter((row) => {
            const ownMatch = row.menuName.toLowerCase().includes(searchLower);
            if (ownMatch) return true;

            if (row.parentMenu === null || row.parentMenu === undefined) {
                return rows.some(
                    (child) =>
                        child.parentMenu === row.menuName &&
                        child.menuName.toLowerCase().includes(searchLower)
                );
            }

            return rows.some(
                (group) =>
                    (group.parentMenu === null || group.parentMenu === undefined) &&
                    group.menuName === row.parentMenu &&
                    group.menuName.toLowerCase().includes(searchLower)
            );
        })
        : rows;

    const groupedRows = buildGroupedRows(filteredRows);

    const handleTableChange = (pagination) => {
        setTablePagination((prev) => ({
            ...prev,
            current: pagination.current,
            pageSize: pagination.pageSize,
        }));
    };

    const isDisabled = submitting || dirtyMenuIds.length === 0;

    if (loading || rolesLoading) {
        return <Spin size="large" style={{ margin: "20px auto" }} />;
    }

    return (
        <div className="active-page-container">
            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>User Role Master</h4>
                </div>
            </div>

            <div className="active-page-body-container">
                <div className="rounded-table-wrapper">
                    <Table
                        columns={columns}
                        dataSource={groupedRows}
                        className="common-pagination common-grid"
                        locale={{
                            emptyText: apiNoResults ? "No records found from API." : "No Data available.",
                        }}
                        expandable={{
                            defaultExpandAllRows: true,
                        }}
                        pagination={false}
                        rowKey={(record) => record.menuId}
                        scroll={{
                            x: "max-content",
                            y: "calc(100vh - 280px)",
                        }}
                        onChange={handleTableChange}
                    />
                </div>
                <div className="book-page-footer-container">
                    <div className="book-page-action-container user-role-master-submit-action">
                        <div></div>
                        <div className="book-page-buttons-container" >
                            <button
                                className="accept-button"
                                data-track={`scanning-submit-${getModeTrackLabel()}`}
                                disabled={isDisabled}
                                onClick={handleSubmit}
                            >
                                {submitting ? "Submitting..." : "Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserRoleMaster;